import { supabase } from './supabase'
import { cheminPage } from './decoupage'
import { televerserGros } from './televersement'
import { preparerImage, dateExif } from './images'
import { extraire, lireZip, type EntreeZip } from './zip'
import type { DocumentChantier, Feuillet } from './types'

/**
 * Import d'une archive Procore (« Archive du projet », fichier ZIP) dans un
 * chantier existant.
 *
 *  - Plans : Procore livre une feuille par PDF, nommée « CODE_TITRE_Rev.N.pdf ».
 *    Chaque feuille devient une page d'un document découpé, avec son code et
 *    son titre déjà inscrits (aucune lecture de cartouche nécessaire). La
 *    dernière révision de chaque feuille va dans « Architecture (Procore) »,
 *    « Structure (Procore) » ou « Autres plans (Procore) » ; les révisions
 *    précédentes vont dans « Anciennes révisions (Procore) ».
 *    Les annotations faites dans Procore sont imprimées dans le PDF : visibles,
 *    mais pas modifiables.
 *  - Photos : comprimées comme une photo téléversée, avec la date EXIF.
 *  - Autres PDF (rapports journaliers, déficiences, QRT, répertoire…) : un
 *    document d'archive par dossier, en lecture seule.
 *
 * L'import peut être relancé sans créer de doublons : ce qui est déjà passé
 * est sauté (identifiants dérivés du nom de chaque fichier).
 */

export interface EtapeImport {
  etape: string
  fait: number
  total: number
}

export interface BilanImport {
  plans: number
  anciennesRevisions: number
  photos: number
  pagesArchives: number
  ignores: string[]
  erreurs: string[]
}

const LIMITE_GRATUIT = 50 * 1024 * 1024

const sansRacine = (nom: string) => nom.split('/').slice(1).join('/')
const dossierDe = (nom: string) => sansRacine(nom).split('/')[0] ?? ''
const fichierDe = (nom: string) => nom.split('/').pop() ?? nom
const estImage = (nom: string) => /\.(jpe?g|png|heic|webp)$/i.test(nom)
const estPdf = (nom: string) => /\.pdf$/i.test(nom)

/** Tri naturel des codes : A-100.2 avant A-101, A-2 avant A-10. */
const triNaturel = (a: string, b: string) => a.localeCompare(b, 'fr', { numeric: true, sensitivity: 'base' })

/** UUID stable tiré d'un texte : relancer l'import ne crée pas de doublon. */
async function uuidStable(texte: string): Promise<string> {
  const h = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(texte)))
  h[6] = (h[6] & 0x0f) | 0x50
  h[8] = (h[8] & 0x3f) | 0x80
  const x = [...h.slice(0, 16)].map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${x.slice(0, 8)}-${x.slice(8, 12)}-${x.slice(12, 16)}-${x.slice(16, 20)}-${x.slice(20, 32)}`
}

interface Feuille {
  entree: EntreeZip
  code: string
  titre: string
  rev: string
}

function lireNomPlan(e: EntreeZip): Feuille {
  const base = fichierDe(e.nom).replace(/\.pdf$/i, '')
  const m = base.match(/^(.+?)_(.*)_Rev\.([^_]+)$/)
  if (!m) return { entree: e, code: base, titre: '', rev: '0' }
  let titre = m[2].replace(/_ /g, ', ').replace(/_/g, ' ').replace(/\s+/g, ' ').trim()
  // Procore a parfois lu l'adresse de l'architecte au lieu du titre : on la
  // laisse vide (le titre se corrige à la main dans l'index du plan).
  if (/\b[A-Z]\d[A-Z] ?\d[A-Z]\d\b/i.test(titre)) titre = ''
  return { entree: e, code: m[1].trim(), titre, rev: m[3] }
}

function famille(code: string): DocumentChantier['type'] {
  const l = code.trim().charAt(0).toUpperCase()
  if (l === 'A') return 'architecture'
  if (l === 'S') return 'structure'
  return 'autre'
}

/** Exécute une liste de tâches, trois à la fois. */
async function parTrois<T>(items: T[], f: (x: T, i: number) => Promise<void>, annule: () => boolean) {
  let suivant = 0
  async function ouvrier() {
    for (;;) {
      if (annule()) return
      const i = suivant++
      if (i >= items.length) return
      await f(items[i], i)
    }
  }
  await Promise.all([ouvrier(), ouvrier(), ouvrier()])
}

async function documentArchive(
  chantierId: string,
  nom: string,
  type: DocumentChantier['type'],
  utilisateur: string
): Promise<DocumentChantier> {
  const { data: existant } = await supabase
    .from('documents')
    .select('*')
    .eq('chantier_id', chantierId)
    .eq('nom', nom)
    .is('supprime_le', null)
    .maybeSingle()
  if (existant) return existant as DocumentChantier
  const id = crypto.randomUUID()
  const { data, error } = await supabase
    .from('documents')
    .insert({
      id,
      chantier_id: chantierId,
      type,
      nom,
      fichier_path: `${chantierId}/pages/${id}/0001.pdf`,
      importe_par: utilisateur
    })
    .select()
    .single()
  if (error) throw error
  return data as DocumentChantier
}

async function pagesDejaLa(doc: DocumentChantier): Promise<Set<string>> {
  const { data } = await supabase.storage
    .from('documents')
    .list(`${doc.chantier_id}/pages/${doc.id}`, { limit: 10000 })
  return new Set((data ?? []).map((f) => f.name))
}

async function envoyerPage(doc: DocumentChantier, n: number, blob: Blob, deja: Set<string>) {
  const nom = `${String(n).padStart(4, '0')}.pdf`
  if (deja.has(nom)) return
  if (blob.size > LIMITE_GRATUIT) {
    throw new Error(
      `page de ${Math.round(blob.size / 1048576)} Mo, au-delà de la limite de 50 Mo du plan gratuit de Supabase`
    )
  }
  const fichier = new File([blob], nom, { type: 'application/pdf' })
  const chemin = cheminPage(doc, n)
  if (fichier.size > 6 * 1024 * 1024) {
    await televerserGros('documents', chemin, fichier, () => {})
  } else {
    const { error } = await supabase.storage
      .from('documents')
      .upload(chemin, fichier, { contentType: 'application/pdf', upsert: true })
    if (error) throw error
  }
}

async function terminerDocument(doc: DocumentChantier, feuillets: Feuillet[], taille: number) {
  for (let i = 0; i < feuillets.length; i += 100) {
    const { error } = await supabase
      .from('feuillets')
      .upsert(
        feuillets.slice(i, i + 100).map((f) => ({ ...f, modifie_le: new Date().toISOString() })),
        { onConflict: 'document_id,page' }
      )
    if (error) throw error
  }
  const { error } = await supabase
    .from('documents')
    .update({ nb_pages: feuillets.length, pages_decoupees: true, taille_octets: taille })
    .eq('id', doc.id)
  if (error) throw error
}

/** Un jeu de feuilles (une feuille = un PDF d'une page) devient un document découpé. */
async function importerFeuilles(
  zip: File,
  chantierId: string,
  utilisateur: string,
  nom: string,
  type: DocumentChantier['type'],
  feuilles: Feuille[],
  avecRevision: boolean,
  bilan: BilanImport,
  progres: (fait: number) => void,
  annule: () => boolean
): Promise<number> {
  const doc = await documentArchive(chantierId, nom, type, utilisateur)
  const deja = await pagesDejaLa(doc)
  const feuillets: Feuillet[] = []
  let taille = 0
  let fait = 0
  await parTrois(
    feuilles,
    async (f, i) => {
      const n = i + 1
      const rev = `rév. ${f.rev}`
      const titre = avecRevision || f.rev !== '0' ? (f.titre ? `${f.titre} — ${rev}` : rev) : f.titre
      feuillets[i] = { document_id: doc.id, page: n, code: f.code, titre: titre || null, source: 'manuel' }
      taille += f.entree.taille
      try {
        if (!deja.has(`${String(n).padStart(4, '0')}.pdf`)) {
          await envoyerPage(doc, n, await extraire(zip, f.entree, 'application/pdf'), deja)
        }
      } catch (e) {
        bilan.erreurs.push(`${fichierDe(f.entree.nom)} : ${e instanceof Error ? e.message : String(e)}`)
      }
      progres(++fait)
    },
    annule
  )
  if (annule()) throw new Error('Import interrompu.')
  await terminerDocument(doc, feuillets, taille)
  return feuilles.length
}

export async function importerArchiveProcore(
  zip: File,
  chantierId: string,
  progression: (e: EtapeImport) => void,
  annule: () => boolean
): Promise<BilanImport> {
  const { data: session } = await supabase.auth.getSession()
  const utilisateur = session.session?.user.id
  if (!utilisateur) throw new Error('Session expirée : reconnecte-toi.')

  const bilan: BilanImport = { plans: 0, anciennesRevisions: 0, photos: 0, pagesArchives: 0, ignores: [], erreurs: [] }

  progression({ etape: "Lecture de l'archive", fait: 0, total: 1 })
  const entrees = await lireZip(zip)
  if (!entrees.some((e) => dossierDe(e.nom) === 'Plans' || dossierDe(e.nom) === 'Photos')) {
    throw new Error("Ce ZIP ne ressemble pas à une archive Procore (aucun dossier Plans ni Photos).")
  }

  // ---------------------------------------------------------------- Plans
  const feuilles = entrees.filter((e) => dossierDe(e.nom) === 'Plans' && estPdf(e.nom)).map(lireNomPlan)
  const parCode = new Map<string, Feuille[]>()
  for (const f of feuilles) parCode.set(f.code, [...(parCode.get(f.code) ?? []), f])
  const actuelles: Feuille[] = []
  const anciennes: Feuille[] = []
  for (const liste of parCode.values()) {
    liste.sort((a, b) => triNaturel(b.rev, a.rev)) // la plus récente d'abord
    actuelles.push(liste[0])
    anciennes.push(...liste.slice(1))
  }
  const totalPlans = feuilles.length
  let faitPlans = 0
  const groupes: { nom: string; type: DocumentChantier['type'] }[] = [
    { nom: 'Architecture (Procore)', type: 'architecture' },
    { nom: 'Structure (Procore)', type: 'structure' },
    { nom: 'Autres plans (Procore)', type: 'autre' }
  ]
  for (const g of groupes) {
    const liste = actuelles.filter((f) => famille(f.code) === g.type).sort((a, b) => triNaturel(a.code, b.code))
    if (liste.length === 0) continue
    const avant = faitPlans
    bilan.plans += await importerFeuilles(zip, chantierId, utilisateur, g.nom, g.type, liste, false, bilan,
      (n) => progression({ etape: 'Plans', fait: avant + n, total: totalPlans }), annule)
    faitPlans = avant + liste.length
  }
  if (anciennes.length > 0) {
    anciennes.sort((a, b) => triNaturel(a.code, b.code) || triNaturel(a.rev, b.rev))
    const avant = faitPlans
    bilan.anciennesRevisions = await importerFeuilles(zip, chantierId, utilisateur,
      'Anciennes révisions (Procore)', 'autre', anciennes, true, bilan,
      (n) => progression({ etape: 'Plans', fait: avant + n, total: totalPlans }), annule)
  }

  // ---------------------------------------------------------------- Photos
  // Les rapports journaliers et les déficiences contiennent souvent les mêmes
  // images que l'outil Photos : on les reconnaît à leur nom (sans le numéro
  // ajouté par Procore) pour ne pas les importer deux fois.
  const cleImage = (nom: string) => fichierDe(nom).replace(/^\d+_/, '').toLowerCase()
  const images = new Map<string, EntreeZip>()
  for (const e of entrees) {
    if (!estImage(e.nom)) continue
    const k = cleImage(e.nom)
    if (!images.has(k) || dossierDe(e.nom) === 'Photos') images.set(k, e)
  }
  const listeImages = [...images.values()]
  const uuids = await Promise.all(listeImages.map((e) => uuidStable(`${chantierId}/procore/${cleImage(e.nom)}`)))
  const dejaPhotos = new Set<string>()
  for (let i = 0; i < uuids.length; i += 200) {
    const { data } = await supabase.from('photos').select('client_uuid').in('client_uuid', uuids.slice(i, i + 200))
    for (const r of data ?? []) dejaPhotos.add((r as { client_uuid: string }).client_uuid)
  }
  let faitPhotos = 0
  await parTrois(
    listeImages,
    async (e, i) => {
      const uuid = uuids[i]
      try {
        if (!dejaPhotos.has(uuid)) {
          const brut = await extraire(zip, e)
          const date = await dateExif(brut)
          const { blob, largeur, hauteur } = await preparerImage(brut)
          const chemin = `${chantierId}/${uuid}.jpg`
          const { error: e1 } = await supabase.storage
            .from('photos')
            .upload(chemin, blob, { contentType: 'image/jpeg', upsert: true })
          if (e1) throw e1
          const dossier = dossierDe(e.nom)
          const { error: e2 } = await supabase.from('photos').insert({
            chantier_id: chantierId,
            prise_par: utilisateur,
            prise_le: (date ?? new Date()).toISOString(),
            source: 'televersee',
            origine_date: date ? 'exif' : 'inconnue',
            note: dossier === 'Photos' ? 'Importée de Procore' : `Importée de Procore (${dossier})`,
            fichier_path: chemin,
            largeur,
            hauteur,
            client_uuid: uuid
          })
          if (e2 && e2.code !== '23505') throw e2
          bilan.photos++
        }
      } catch (err) {
        bilan.erreurs.push(`${fichierDe(e.nom)} : ${err instanceof Error ? err.message : String(err)}`)
      }
      progression({ etape: 'Photos', fait: ++faitPhotos, total: listeImages.length })
    },
    annule
  )
  if (annule()) throw new Error('Import interrompu.')

  // ---------------------------------------------------------------- Archives PDF
  const autres = entrees.filter((e) => estPdf(e.nom) && dossierDe(e.nom) !== 'Plans')
  const parDossier = new Map<string, EntreeZip[]>()
  for (const e of autres) {
    const parties = sansRacine(e.nom).split('/')
    const d = parties.length > 1 ? parties[0] : 'Divers'
    parDossier.set(d, [...(parDossier.get(d) ?? []), e])
  }
  for (const e of entrees) {
    if (!estPdf(e.nom) && !estImage(e.nom) && !/path-map\.csv$/i.test(e.nom)) bilan.ignores.push(sansRacine(e.nom))
  }

  const { PDFDocument } = await import('pdf-lib')
  const dossiers = [...parDossier.keys()].sort(triNaturel)
  let faitArch = 0
  for (const d of dossiers) {
    if (annule()) throw new Error('Import interrompu.')
    const liste = parDossier.get(d)!.sort((a, b) => triNaturel(sansRacine(a.nom), sansRacine(b.nom)))
    const doc = await documentArchive(chantierId, `${d} (Procore)`, 'autre', utilisateur)
    const deja = await pagesDejaLa(doc)
    const feuillets: Feuillet[] = []
    let taille = 0
    let n = 0
    for (const e of liste) {
      if (annule()) throw new Error('Import interrompu.')
      const nomFichier = fichierDe(e.nom).replace(/\.pdf$/i, '')
      // Sous-dossiers (ex. une déficience et ses pièces jointes) : gardés dans le libellé.
      const sous = sansRacine(e.nom).split('/').slice(1, -1).join(' / ')
      const libelle = sous && sous !== nomFichier ? `${sous} / ${nomFichier}` : nomFichier
      try {
        const source = await PDFDocument.load(await (await extraire(zip, e)).arrayBuffer(), {
          ignoreEncryption: true,
          updateMetadata: false
        })
        const nb = source.getPageCount()
        taille += e.taille
        for (let k = 0; k < nb; k++) {
          n++
          if (!deja.has(`${String(n).padStart(4, '0')}.pdf`)) {
            const une = await PDFDocument.create()
            const [copie] = await une.copyPages(source, [k])
            une.addPage(copie)
            const octets = await une.save({ useObjectStreams: true })
            await envoyerPage(doc, n, new Blob([new Uint8Array(octets) as BlobPart], { type: 'application/pdf' }), deja)
          }
          feuillets.push({
            document_id: doc.id,
            page: n,
            code: null,
            titre: nb > 1 ? `${libelle} (p. ${k + 1}/${nb})` : libelle,
            source: 'manuel'
          })
          bilan.pagesArchives++
        }
      } catch (err) {
        bilan.erreurs.push(`${sansRacine(e.nom)} : ${err instanceof Error ? err.message : String(err)}`)
      }
      progression({ etape: 'Documents d\'archive', fait: ++faitArch, total: autres.length })
    }
    if (feuillets.length > 0) await terminerDocument(doc, feuillets, taille)
  }

  return bilan
}
